import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { FiMessageCircle } from 'react-icons/fi';

import { useTranslation } from 'react-i18next';

import WithBackground from '../Common/WithBackground';
import { PrivacyAcceptance } from '../PrivacyPolicy';

import './style.css';

const EmployerKontakt = ({ bg, overlay }) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    message: '',
    acceptPrivacy: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateField = (name, value) => {
    const errors = {};

    switch (name) {
      case 'companyName':
        if (!value.trim()) {
          errors.companyName = t('validation.companyNameRequired');
        } else if (value.trim().length < 2) {
          errors.companyName = t('validation.companyNameMinLength');
        } else if (value.trim().length > 200) {
          errors.companyName = t('validation.companyNameMaxLength');
        }
        break;

      case 'contactPerson':
        if (!value.trim()) {
          errors.contactPerson = t('validation.contactPersonRequired');
        } else if (value.trim().length < 2) {
          errors.contactPerson = t('validation.contactPersonMinLength');
        } else if (value.trim().length > 100) {
          errors.contactPerson = t('validation.contactPersonMaxLength');
        }
        break;

      case 'email':
        if (!value.trim()) {
          errors.email = t('validation.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = t('validation.emailInvalid');
        }
        break;

      case 'phone':
        if (!value.trim()) {
          errors.phone = t('validation.phoneRequired');
        } else if (!/^[+]?[\d\s\-()]{8,15}$/.test(value.replace(/\s/g, ''))) {
          errors.phone = t('validation.phoneInvalid');
        }
        break;

      case 'message':
        if (value.trim() && value.trim().length < 10) {
          errors.message = t('validation.messageMinLength');
        } else if (value.trim().length > 3000) {
          errors.message = t('validation.messageMaxLength');
        }
        break;
    }

    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (type !== 'checkbox' && value.trim()) {
      const fieldErrors = validateField(name, value);
      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};

    const requiredFields = ['companyName', 'contactPerson', 'email', 'phone'];
    requiredFields.forEach((field) => {
      const fieldErrors = validateField(field, formData[field]);
      Object.assign(errors, fieldErrors);
    });

    if (formData.message.trim()) {
      const messageErrors = validateField('message', formData.message);
      Object.assign(errors, messageErrors);
    }

    if (!formData.acceptPrivacy) {
      errors.acceptPrivacy = t('validation.privacyRequired');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);

    const errors = validateForm();
    setFormErrors(errors);

    console.log('EmployerKontakt validation errors:', errors);

    if (Object.keys(errors).length > 0) {
      const firstErrorField = document.querySelector(
        '.form-input--error, .form-textarea--error',
      );
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstErrorField.focus();
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const emailData = {
        name: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        message: `ZAPYTANIE OD PRACODAWCY

Nazwa firmy: ${formData.companyName}
Osoba kontaktowa: ${formData.contactPerson}
Email: ${formData.email}
Telefon: ${formData.phone}

${formData.message.trim() ? `Szczegóły zlecenia:\n${formData.message}` : 'Brak dodatkowych szczegółów.'}`,
        companyName: formData.companyName,
        contactPerson: formData.contactPerson,
        formType: 'employer_contact',
      };

      const apiUrl = process.env.REACT_APP_API_URL;

      const response = await fetch(`${apiUrl}/contact.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status === 'success') {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          message: '',
          acceptPrivacy: false,
        });

        console.log('✅ Employer contact form sent successfully');
      } else {
        throw new Error(result.message || 'Email sending failed');
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('❌ Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const kontaktContent = (
    <section className='employer-kontakt section' id='contact'>
      <div className='employer-kontakt__container container'>
        <div className='employer-kontakt__content'>
          <div className='employer-kontakt__right'>
            <div className='employer-kontakt__form-section'>
              <div className='form-header'>
                <h3 className='form-header__title'>
                  {t('employerKontakt.form.title')}
                </h3>
                <p className='form-header__subtitle'>
                  {t('employerKontakt.form.subtitle')}
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className='contact-form__message contact-form__message--success'>
                  <strong>{t('employerKontakt.form.successMessage')}</strong>
                  <br />
                  {t('employerKontakt.form.successDescription')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className='contact-form__message contact-form__message--error'>
                  <strong>{t('employerKontakt.form.errorMessage')}</strong>
                  <br />
                  {t('employerKontakt.form.errorDescription')}
                </div>
              )}

              <form className='contact-form' onSubmit={handleSubmit}>
                <div className='form-row'>
                  <div className='form-group'>
                    <label htmlFor='companyName' className='form-label'>
                      {t('employerKontakt.form.companyName')}
                    </label>
                    <input
                      type='text'
                      id='companyName'
                      name='companyName'
                      value={formData.companyName}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.companyName ? 'form-input--error' : ''}`}
                      required
                    />
                    {formErrors.companyName && (
                      <span className='form-error'>
                        {formErrors.companyName}
                      </span>
                    )}
                  </div>

                  <div className='form-group'>
                    <label htmlFor='contactPerson' className='form-label'>
                      {t('employerKontakt.form.contactPerson')}
                    </label>
                    <input
                      type='text'
                      id='contactPerson'
                      name='contactPerson'
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.contactPerson ? 'form-input--error' : ''}`}
                      required
                    />
                    {formErrors.contactPerson && (
                      <span className='form-error'>
                        {formErrors.contactPerson}
                      </span>
                    )}
                  </div>
                </div>

                <div className='form-row'>
                  <div className='form-group'>
                    <label htmlFor='email' className='form-label'>
                      {t('employerKontakt.form.email')}
                    </label>
                    <input
                      type='email'
                      id='email'
                      name='email'
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.email ? 'form-input--error' : ''}`}
                      required
                    />
                    {formErrors.email && (
                      <span className='form-error'>{formErrors.email}</span>
                    )}
                  </div>

                  <div className='form-group'>
                    <label htmlFor='phone' className='form-label'>
                      {t('employerKontakt.form.phone')}
                    </label>
                    <input
                      type='tel'
                      id='phone'
                      name='phone'
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${formErrors.phone ? 'form-input--error' : ''}`}
                      required
                    />
                    {formErrors.phone && (
                      <span className='form-error'>{formErrors.phone}</span>
                    )}
                  </div>
                </div>

                <div className='form-group'>
                  <label htmlFor='message' className='form-label'>
                    {t('employerKontakt.form.message')}
                  </label>
                  <textarea
                    id='message'
                    name='message'
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`form-textarea ${formErrors.message ? 'form-textarea--error' : ''}`}
                    rows='5'
                    placeholder={t('employerKontakt.form.messagePlaceholder')}
                  ></textarea>
                  {formErrors.message && (
                    <span className='form-error'>{formErrors.message}</span>
                  )}
                </div>

                <div className='form-group form-group--checkbox'>
                  <PrivacyAcceptance
                    checked={formData.acceptPrivacy}
                    onChange={(checked) => {
                      setFormData((prev) => ({
                        ...prev,
                        acceptPrivacy: checked,
                      }));
                      if (formErrors.acceptPrivacy && checked) {
                        setFormErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.acceptPrivacy;
                          return newErrors;
                        });
                      }
                    }}
                    required
                  />
                  {formErrors.acceptPrivacy && (
                    <span className='form-error'>
                      {formErrors.acceptPrivacy}
                    </span>
                  )}
                </div>

                <div className='form-submit'>
                  <button
                    type='submit'
                    className='form-submit__button btn'
                    disabled={!formData.acceptPrivacy || isSubmitting}
                  >
                    <FiMessageCircle />
                    {isSubmitting
                      ? t('employerKontakt.form.submitting')
                      : t('employerKontakt.form.submitButton')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {kontaktContent}
    </WithBackground>
  ) : (
    kontaktContent
  );
};

EmployerKontakt.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

EmployerKontakt.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default EmployerKontakt;
